import React, { useState } from "react";
import { format } from "date-fns";
import { ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CampaignPurchase, PurchaseSortField, SortDirection, CAMPAIGN_PURCHASES_QUERY_KEY } from "@/hooks/useAdminCampaignPurchases";

interface CampaignPurchasesTableProps {
  purchases: CampaignPurchase[];
  isLoading: boolean;
  isEmpty: boolean;
  sortField: PurchaseSortField;
  sortDirection: SortDirection;
  onSort: (field: PurchaseSortField) => void;
}

const CampaignPurchasesTable: React.FC<CampaignPurchasesTableProps> = ({
  purchases,
  isLoading,
  isEmpty,
  sortField,
  sortDirection,
  onSort,
}) => {
  const queryClient = useQueryClient();
  const [selectedBuyer, setSelectedBuyer] = useState<CampaignPurchase["profiles"] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [purchaseToDelete, setPurchaseToDelete] = useState<CampaignPurchase | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (purchase: CampaignPurchase) => {
      let creditsDeducted = false;
      
      // Try to deduct credits from the user's balance
      if (purchase.credits_granted > 0) {
        const { data, error: updateError } = await supabase.rpc("deduct_user_credits", {
          p_user_id: purchase.user_id,
          p_credits_to_deduct: purchase.credits_granted
        });
        
        if (updateError) {
          console.error("Failed to deduct credits:", updateError);
          throw new Error("Failed to deduct credits: " + updateError.message);
        }
        
        const result = data as { success?: boolean; error?: string } | null;
        
        if (result && !result.success) {
          // Allow deletion if user membership doesn't exist (user was deleted)
          if (result.error === "User membership not found") {
            console.warn("User membership not found, proceeding with purchase deletion");
          } else {
            // Block on other errors (e.g., insufficient credits)
            throw new Error(result.error || "Failed to deduct credits");
          }
        } else {
          creditsDeducted = true;
        }
      }
      
      // Delete the purchase record
      const { error } = await supabase
        .from("campaign_purchases")
        .delete()
        .eq("id", purchase.id);
      if (error) throw error;
      
      return { creditsDeducted };
    },
    onSuccess: (result) => {
      if (result.creditsDeducted) {
        toast.success("Purchase deleted and credits deducted");
      } else {
        toast.success("Purchase deleted (credits not deducted)");
      }
      queryClient.invalidateQueries({ queryKey: [CAMPAIGN_PURCHASES_QUERY_KEY] });
      setPurchaseToDelete(null);
    },
    onError: (error) => {
      toast.error("Failed to delete purchase: " + error.message);
    },
  });

  const SortIcon = ({ field }: { field: PurchaseSortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === "asc" 
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const SortableHeader = ({ field, children }: { field: PurchaseSortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 select-none whitespace-nowrap h-9 text-xs"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon field={field} />
      </div>
    </TableHead>
  );

  const handleBuyerClick = (purchase: CampaignPurchase) => {
    setSelectedBuyer(purchase.profiles);
    setSelectedUserId(purchase.user_id);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <p className="text-muted-foreground">Loading purchases...</p>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex justify-center items-center py-12">
        <p className="text-muted-foreground">No purchases found</p>
      </div>
    );
  }

  return (
    <>
      <AlertDialog open={!!purchaseToDelete} onOpenChange={() => setPurchaseToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this purchase by{" "}
              <span className="font-medium">
                {purchaseToDelete?.profiles?.first_name} {purchaseToDelete?.profiles?.last_name}
              </span>
              ? This will permanently remove the purchase record and deduct the granted credits from the user's balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => purchaseToDelete && deleteMutation.mutate(purchaseToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!selectedBuyer} onOpenChange={() => setSelectedBuyer(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Buyer Details</DialogTitle>
          </DialogHeader>
          {selectedBuyer && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-muted-foreground">Name:</span>
                <span className="font-medium">
                  {selectedBuyer.first_name} {selectedBuyer.last_name}
                </span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-muted-foreground">Email:</span>
                <span className="font-medium break-all">{selectedBuyer.email || "N/A"}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-muted-foreground">Phone:</span>
                <span className="font-medium">{selectedBuyer.phone || "N/A"}</span>
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2">
                <span className="text-muted-foreground">User ID:</span>
                <span className="font-mono text-xs break-all">{selectedUserId}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent bg-muted">
            <SortableHeader field="created_at">Date</SortableHeader>
            <SortableHeader field="campaign">Campaign</SortableHeader>
            <SortableHeader field="package">Package</SortableHeader>
            <SortableHeader field="buyer">Buyer</SortableHeader>
            <SortableHeader field="price_paid">Paid</SortableHeader>
            <SortableHeader field="credits_granted">Credits</SortableHeader>
            <TableHead className="whitespace-nowrap h-9 text-xs">Payment</TableHead>
            <TableHead className="whitespace-nowrap h-9 text-xs w-[50px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {purchases.map((purchase) => (
            <TableRow key={purchase.id} className="h-9">
              <TableCell className="py-1 whitespace-nowrap text-xs">
                {purchase.created_at && format(new Date(purchase.created_at), "dd MMM yyyy HH:mm")}
              </TableCell>
              <TableCell className="py-1 text-xs max-w-[150px] truncate">
                {purchase.campaign_packages?.campaigns?.name || "Unknown"}
              </TableCell>
              <TableCell className="py-1 text-xs max-w-[120px] truncate">
                {purchase.campaign_packages?.name || "Unknown"}
              </TableCell>
              <TableCell className="py-1">
                <button
                  onClick={() => handleBuyerClick(purchase)}
                  className="text-xs text-primary hover:underline text-left"
                >
                  {purchase.profiles?.first_name} {purchase.profiles?.last_name}
                </button>
              </TableCell>
              <TableCell className="py-1 whitespace-nowrap text-xs font-medium">
                £{purchase.price_paid?.toFixed(2)}
              </TableCell>
              <TableCell className="py-1 whitespace-nowrap text-xs">
                {purchase.credits_granted}
              </TableCell>
              <TableCell className="py-1 whitespace-nowrap text-xs">
                {purchase.stripe_payment_intent ? (
                  <span className="text-green-600">Stripe</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="py-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setPurchaseToDelete(purchase)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default CampaignPurchasesTable;
